import React from 'react';
import { Sparkles } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { trackButtonClick } from '../../firebaseConfig';
import { useAnalytics } from '../../lib/AnalyticsContext';

interface MobileMenuProps {
  isOpen: boolean;
}

export function MobileMenu({ isOpen }: MobileMenuProps) {
  const { trackUserAction } = useAnalytics();
  const navigate = useNavigate();
  const location = useLocation();
  const isLandingPage = location.pathname === '/';

  const scrollToSection = (id: string) => {
    // Track mobile navigation link click
    trackButtonClick(`Mobile Navigation: ${id}`, 'Navbar');
    trackUserAction('navigation', {
      target_section: id,
      source: 'mobile_menu'
    });

    if (isLandingPage) {
      // If already on landing page, just scroll to the section
      const element = document.getElementById(id);
      if (element) {
        const navbarHeight = 100; // Account for fixed navbar
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth"
        });
      }
    } else {
      // If on another page, navigate to landing page with section hash
      navigate(`/#${id}`);
    }
  };

  const navigateToPricing = () => {
    // Track navigation to pricing
    trackButtonClick('Mobile Navigation: pricing', 'Navbar');
    trackUserAction('navigation', {
      target_page: 'pricing',
      source: 'mobile_menu'
    });

    // Navigate to pricing page
    navigate('/pricing');
  };

  const handleTryMockTrail = () => {
    // Track try mocktrail button click from mobile menu
    trackButtonClick('Try MockTrail', 'Mobile Menu');
    trackUserAction('conversion_action', {
      action: 'try_mocktrail',
      location: 'mobile_menu'
    });
    const platformUrl = import.meta.env.VITE_MOCKTRAIL_PLATFORM_URL;
    // Open the app page in a new tab
    window.open(`${platformUrl}/login?utm_source=landing_page&utm_medium=mobile_menu&utm_campaign=try_mocktrail`, '_blank');
  };

  return (
    <div 
      className={`
        lg:hidden border-t border-border-light dark:border-border-dark 
        overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]
        ${isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}
      `}
    >
      <div className="px-6 py-6 flex flex-col gap-6">
        <div className="flex flex-col space-y-4">
          <button 
            onClick={() => scrollToSection('features')} 
            className={`
              nav-link-mobile transition-all duration-500 ease-out
              ${isOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'}
            `}
            style={{ transitionDelay: isOpen ? '120ms' : '0ms' }}
          >
            Features
          </button>
          <button 
            onClick={navigateToPricing} 
            className={`
              nav-link-mobile transition-all duration-500 ease-out
              ${isOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'}
            `}
            style={{ transitionDelay: isOpen ? '220ms' : '0ms' }}
          >
            Pricing
          </button>
          <button 
            onClick={() => scrollToSection('how-it-works')} 
            className={`
              nav-link-mobile transition-all duration-500 ease-out
              ${isOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'}
            `}
            style={{ transitionDelay: isOpen ? '170ms' : '0ms' }}
          >
            How It Works
          </button>
          <button 
            onClick={() => scrollToSection('testimonials')} 
            className={`
              nav-link-mobile transition-all duration-500 ease-out
              ${isOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'}
            `}
            style={{ transitionDelay: isOpen ? '270ms' : '0ms' }}
          >
            Testimonials
          </button>
          <button 
            onClick={() => navigate('/ambassador-program')} 
            className={`
              nav-link-mobile text-secondary-light dark:text-secondary-dark transition-all duration-500 ease-out
              ${isOpen ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'}
            `}
            style={{ transitionDelay: isOpen ? '320ms' : '0ms' }}
          >
            Earn with MockTrail
          </button>
        </div>
        <div className="flex flex-col items-center gap-4 pt-4 border-t border-border-light dark:border-border-dark">
          <button 
            onClick={handleTryMockTrail}
            className={`
              btn-primary flex items-center justify-center gap-2 w-full 
              transition-all duration-500 ease-out transform
              ${isOpen ? 'translate-y-0 opacity-100 scale-100' : '-translate-y-4 opacity-0 scale-95'}
            `}
            style={{ transitionDelay: isOpen ? '320ms' : '0ms' }}
          >
              {/* Button shine effect */}
          <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 -translate-x-full group-hover:translate-x-full transition-all duration-1000 ease-out"></span>
            Sign Up
            <Sparkles className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" />
          </button>
        </div>
      </div>
    </div>
  );
}